import { isKnownPaymentMethodCode, isKnownPaymentSubjectCode, paymentMethodOf, paymentObjectOf } from './fiscalCodes'

/**
 * Фискальный профиль строки продажи (договор CASH-CONTRACTS §2): проекция выбранной карточки с явным источником. `null` по любому
 * реквизиту — «не задан»: продажа такой строки блокируется ДО оплаты, умолчаний нет (ни «товар», ни «полный расчёт»). Ставка НДС
 * живёт отдельно (`CartLine.vat`): `none`, `vat0` и «неизвестно» различаются там же.
 */
export interface SaleProfile {
  /** Предмет расчёта, код ФФД 1..33 (тег 1212). */
  paymentSubject: number | null
  /** Способ расчёта, код ФФД 1..7 (тег 1214). */
  paymentMethod: number | null
  /** Маркированный товар: фискализации маркировки у агента нет, строка блокируется. */
  isMarked: boolean | null
  /** Весовой ли товар: `null` блокирует (весовой товар не продаётся как штучный молча). */
  isWeighted: boolean | null
  /** Код единицы по ОКЕИ и подпись для чека. */
  unitCode: string | null
  unitName: string | null
  /** Версия карточки на момент чтения: по ней видно, что снимок строки не менялся. */
  cardVersion: number | null
  /** Откуда взята карточка: ровно один источник, он виден кассиру. */
  origin: SaleProfileOrigin | null
}

export interface SaleProfileOrigin {
  resourceId: string
  /** Вид каталога; значения проверяет пакет вида, общий код по виду не ветвится. */
  kind: string
  key: string
}

/** Причины блокировки строки по профилю; слова договора, плюс две клиентские (`payment-method-unsupported`, `profile-missing`). */
export type ProfileBlockReason =
  | 'profile-missing'
  | 'payment-subject-not-set'
  | 'payment-subject-unsupported'
  | 'payment-method-not-set'
  | 'payment-method-unsupported'
  | 'marked-not-supported'
  | 'marked-unknown'
  | 'unit-not-set'
  | 'weight-unknown'

const TEXTS: Readonly<Record<ProfileBlockReason, string>> = {
  'profile-missing': 'не заполнены кассовые реквизиты',
  'payment-subject-not-set': 'не указан предмет расчёта',
  'payment-subject-unsupported': 'предмет расчёта не поддерживается кассой',
  'payment-method-not-set': 'не указан способ расчёта',
  'payment-method-unsupported': 'способ расчёта не поддерживается кассой',
  'marked-not-supported': 'маркированный товар касса пока не принимает',
  'marked-unknown': 'неизвестно, маркированный ли товар',
  'unit-not-set': 'не указана единица измерения',
  'weight-unknown': 'неизвестно, весовой ли товар',
}

/** Почему профиль не годится для продажи; пустой список — годится. Порядок стабилен: сначала то, что исправляется в карточке. */
export function profileBlockReasons(profile: SaleProfile | null): readonly ProfileBlockReason[] {
  if (profile === null) return ['profile-missing']
  const reasons: ProfileBlockReason[] = []
  if (profile.paymentSubject === null) reasons.push('payment-subject-not-set')
  else if (paymentObjectOf(profile.paymentSubject) === null || !isKnownPaymentSubjectCode(profile.paymentSubject)) reasons.push('payment-subject-unsupported')
  if (profile.paymentMethod === null) reasons.push('payment-method-not-set')
  else if (paymentMethodOf(profile.paymentMethod) === null || !isKnownPaymentMethodCode(profile.paymentMethod)) reasons.push('payment-method-unsupported')
  // Маркировка неизвестна — продажу не открывает: сомнение не равно «не маркирован».
  if (profile.isMarked === true) reasons.push('marked-not-supported')
  else if (profile.isMarked === null) reasons.push('marked-unknown')
  if (profile.unitCode === null || profile.unitName === null) reasons.push('unit-not-set')
  if (profile.isWeighted === null) reasons.push('weight-unknown')
  return reasons
}

export const profileReasonText = (reason: ProfileBlockReason): string => TEXTS[reason]

/** Профили равны по всем реквизитам: строки с разными профилями в одну не складываются. */
export function sameProfile(a: SaleProfile | null, b: SaleProfile | null): boolean {
  if (a === null || b === null) return a === b
  return (
    a.paymentSubject === b.paymentSubject &&
    a.paymentMethod === b.paymentMethod &&
    a.isMarked === b.isMarked &&
    a.isWeighted === b.isWeighted &&
    a.unitCode === b.unitCode &&
    a.unitName === b.unitName &&
    a.cardVersion === b.cardVersion &&
    a.origin?.resourceId === b.origin?.resourceId &&
    a.origin?.kind === b.origin?.kind &&
    a.origin?.key === b.origin?.key
  )
}
